import { useEffect } from 'react';

/**
 * Per-user browser-tab favicon override for the sales portal.
 *
 * A couple of specific users asked for a custom tab icon (kanban 806e60af);
 * everyone else keeps the ChemMasters brand favicon wired in index.html.
 * Mounted from PortalLayout, so the override only applies while the user is
 * inside the portal — the effect cleanup restores the default icons when
 * they navigate away, log out, or get impersonated away from an allowlisted
 * account.
 */
export const PERSONAL_FAVICON_HREF = '/cat-favicon-96x96.png';

const PERSONAL_FAVICON_EMAILS = new Set([
  'emily@chemmasters.net',
  'roblyn@chemmasters.net',
]);

export function hasPersonalFavicon(email: string | null | undefined): boolean {
  return email != null && PERSONAL_FAVICON_EMAILS.has(email.trim().toLowerCase());
}

export function usePersonalFavicon(email: string | null | undefined): void {
  const enabled = hasPersonalFavicon(email);

  useEffect(() => {
    if (!enabled) return;

    const head = document.head;
    // Detach the default icon links: any <link rel="icon"> left in the head
    // competes with (and, for the PNG, wins over) the override.
    const defaults = Array.from(
      head.querySelectorAll<HTMLLinkElement>('link[rel="icon"]'),
    );
    defaults.forEach((link) => link.remove());

    const override = document.createElement('link');
    override.rel = 'icon';
    override.type = 'image/png';
    override.setAttribute('sizes', '96x96');
    override.href = PERSONAL_FAVICON_HREF;
    head.appendChild(override);

    return () => {
      override.remove();
      defaults.forEach((link) => head.appendChild(link));
    };
  }, [enabled]);
}
